import ZoomInIcon from '@mui/icons-material/ZoomIn'
import ZoomOutIcon from '@mui/icons-material/ZoomOut'
import { useCallback, useEffect, useState } from 'react'
import Cropper, { type Area } from 'react-easy-crop'
import { Box, IconButton, Slider, Stack } from '@mui/material'

import { cropImage } from '~/lib/imageProcessing'

const MIN_ZOOM = 1
const MAX_ZOOM = 10

export const ImageCrop = (props: { src: string; onCrop(crop: Area | null): void }) => {
  const handleCropComplete = useCallback(
    (_croppedArea: Area, croppedAreaPixels: Area) => {
      props.onCrop(croppedAreaPixels)
    },
    [props],
  )

  const [crop, onCropChange] = useState({ x: 0, y: 0 })
  const [zoom, onZoomChange] = useState(MIN_ZOOM)

  return (
    <Box>
      <Box position="relative" height={500}>
        <Cropper
          image={props.src}
          aspect={1}
          crop={crop}
          zoom={zoom}
          maxZoom={MAX_ZOOM}
          style={{
            cropAreaStyle: {
              border: '3px solid #00A0FF',
            },
          }}
          onCropChange={onCropChange}
          onZoomChange={onZoomChange}
          onCropComplete={handleCropComplete}
          showGrid={false}
        />
      </Box>

      <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        gap={{ xs: 2, sm: 2 }}
        mt={{ xs: 2, sm: 4 }}
        px={{ xs: 0, sm: 8 }}
      >
        <IconButton onClick={() => onZoomChange((prev) => Math.max(MIN_ZOOM, prev - 0.1))}>
          <ZoomOutIcon />
        </IconButton>
        <Slider
          step={0.1}
          value={zoom}
          defaultValue={MIN_ZOOM}
          max={MAX_ZOOM}
          min={MIN_ZOOM}
          onChange={(_, value) => onZoomChange(value as number)}
        />
        <IconButton onClick={() => onZoomChange((prev) => Math.min(MAX_ZOOM, prev + 0.1))}>
          <ZoomInIcon />
        </IconButton>
      </Stack>
    </Box>
  )
}

export const useCropImage = (
  image: Blob,
): {
  cropped: boolean
  crop(): Promise<Blob>
  props: React.ComponentProps<typeof ImageCrop>
} => {
  const [area, setArea] = useState<Area | null>(null)

  const [objectURL, setObjectURL] = useState('')

  useEffect(() => {
    const url = URL.createObjectURL(image)
    setObjectURL(url)
    return () => {
      URL.revokeObjectURL(url)
    }
  }, [image])

  return {
    cropped: Boolean(objectURL && area),
    async crop() {
      if (!area) throw new Error('Area not selected')
      return await cropImage(image, area)
    },
    props: { src: objectURL, onCrop: setArea },
  }
}
